import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { InputIcon } from '@motorway/motorway-storybook-cra';

import cypressIds from 'CypressId';

import { GA_TAGS } from 'Utilities/analytics';
import { applyCypressData } from 'Utilities/index';
import { HELP_URL } from 'Utilities/urls';
import { ULEZ_PAGE_ROUTES, VALUATION_TRACKER_ROUTES } from 'Utilities/wordpress';

import { useBreakpoint } from '../../../components/misc/Breakpoints/Breakpoints';
import { routes } from '../../../routes';
/* Localisation strings */
import Texts from '../../../texts.json';

import HeaderLinksMobile from './HeaderLinksMobile';

import styles from './Header.scss';

const LocalT = new MDText(Texts);

const HeaderLinks = () => {
  const [activeGroup, setActiveGroup] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.maxWidth.breakpointTablet;

  const navRoutes = routes.filter((route) => route.nav);

  const isExternalLink = (r) => /^https?:\/\//.test(r);

  const fireGAEventForLink = (path) => {
    switch (path) {
      case VALUATION_TRACKER_ROUTES[0]:
        return GA_TAGS.NEW_NAV_TRACKER_LINK_CLICKED();

      case ULEZ_PAGE_ROUTES[0]:
        return GA_TAGS.NEW_NAV_ULEZ_LINK_CLICKED();

      default: return null;
    }
  };

  const renderRoute = (route) => (
    <li
      key={`navlink-${route.path}-${route.label}`}
      className={cx(styles.navItem, styles.topLevel)}
      data-name={route.name}
    >
      {isExternalLink(route.path) ? (
        <>
          {route.path === HELP_URL ? (
            <a href={route.path} onClick={() => GA_TAGS.HEADER_HELP_LINK_CLICKED()} rel="noopener noreferrer" target="_blank">
              {LocalT.translate(route.label)}
            </a>
          ) : (
            <a href={route.path} rel="noopener noreferrer" target="_blank">
              {LocalT.translate(route.label)}
            </a>
          )}
        </>
      ) : (
        <Link to={route.path} >
          {LocalT.translate(route.label)}
        </Link>
      )}
    </li>
  );

  const renderSubRoutes = (route) => route.subRoutes.map((subRoute) => (
    <li
      key={`navlink-${route.path}-sub-${subRoute.label}`}
      className={cx(styles.navItem, {
        [styles.withCaption]: subRoute.caption,
      })}
    >
      {isExternalLink(subRoute.path) ? (
        <a href={subRoute.path} rel="noopener noreferrer" target="_blank">
          {LocalT.translate(subRoute.label)}
        </a>
      ) : (
        <Link onClick={() => fireGAEventForLink(subRoute.path)} to={subRoute.path}>
          {subRoute.icon && (
            <div className={cx(styles.icon, styles[subRoute.icon])} />
          )}
          <div>
              <h6 className={ cx(styles.label, {
                [styles.withBadge]: subRoute?.badge || false,
              }) }>
                { subRoute?.badge && <span className={ styles.badge }>
                  { LocalT.translate('components.navigation.badgeLabel') }
                </span> }
              { LocalT.translate(subRoute.label) }
            </h6>
            {subRoute.caption && (
              <span className={styles.caption}>{LocalT.translate(subRoute.caption)}</span>
            )}
          </div>
        </Link>
      )}
    </li>
  ));

  return (
    <>
      <a
        className={cx(styles.mobileMoreMenu)}
        {...applyCypressData(cypressIds.menus.mobileHamburgerMenu)}
        href="#menu"
        onClick={(e) => {
          e.preventDefault();

          setMenuOpen(!menuOpen);
        }}
      >
        {LocalT.translate('components.navigation.openMenuLabel')}
        <span />
      </a>

      <nav className={cx(styles.nav, styles.links, styles.navDesktop)}>
      <ul {...applyCypressData(cypressIds.menus.desktopMenu)}>
          {navRoutes.map((route) => {
            if (route.subRoutes) {
              return (
                <li
                  key={`navlink-${route.name}-${route.label}`}
                  className={styles.navItem}
                  data-name={route.name}
                >
                  <div
                    className={cx(styles.toggle, {
                      [styles.active]: activeGroup === route.label,
                    })}
                  >
                    <div
                      className={styles.groupToggle}
                    >
                      <span>{LocalT.translate(route.label)}</span>
                      <InputIcon className="mw-input-icon" icon="select" />
                    </div>
                    <div className={cx(styles.subMenu)}>
                      <ul>{renderSubRoutes(route)}</ul>
                    </div>
                  </div>
                </li>
              );
            }
            return renderRoute(route);
          })}
        </ul>
      </nav>
      {
        isMobile && (
          <HeaderLinksMobile
            {...{
              menuOpen,
              setMenuOpen,
            }}
          >
            <ul {...applyCypressData(cypressIds.menus.mobileMenu)}>
              {navRoutes.map((route) => {
                if (route.subRoutes) {
                  const active = (activeGroup === route.label);

                  return (
                    <li
                      key={`navlink-${route.name}-${route.label}`}
                      className={styles.navItem}
                      data-cy={route.name}
                      data-name={route.name}
                    >
                      <div
                        className={cx(styles.toggle, {
                          [styles.active]: active,
                        })}
                      >
                        <div
                          aria-controls={route.name}
                          aria-expanded={active}
                          className={styles.groupToggle}
                          onClick={() => setActiveGroup(
                            activeGroup === route.label ? null : route.label,
                          )}
                        >
                          {LocalT.translate(route.label)}
                          <InputIcon icon="select" />
                        </div>
                        <div className={cx(styles.subMenu)} id={route.name}>
                          <ul>{renderSubRoutes(route)}</ul>
                        </div>
                      </div>
                    </li>
                  );
                }
                return renderRoute(route);
              })}
            </ul>
          </HeaderLinksMobile>
        )
      }
    </>
  );
};

export default HeaderLinks;
